import React from "react";
import { Box, styled, TextField, Checkbox } from "@mui/material";
import FormControlLabel from '@mui/material/FormControlLabel';
import HelpIcon from '@mui/icons-material/Help';
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { Typography } from "@mui/material";
import ModalSimpleButton from "../../../components/Modal/ModalSimpleButton";
import EmailAddressTextField from "../../../components/EmailAddressField";
import AuditInfoLabel from "../../../components/AuditInfoLabel";
import { exxonMobilDomains } from "../../../global";

const StyledBox = styled(Box)(() => ({
  display: 'flex',
  marginBottom: "20px"
}));

const ExternalLaboratoryForm = ({ 
  externalLabData, 
  formikLabRef, 
  invalidExternalLabNames=[],
  invalidLabCodes=[], 
  canEdit=false,
  isNewExternalLab=false,
  onSiteContactEmailError=false,
  setOnSiteContactEmailError
}) => {

  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [modalContent, setModalContent] = React.useState({ title: '', message: '' });

  const initialValues = {
    laboratoryName: externalLabData?.laboratoryName ?? '',
    labCode: externalLabData?.labCode ?? '',
    isActive: externalLabData?.isActive ?? false,
    labContactEmailAddress: externalLabData?.labContactEmailAddress ?? '',
    onSiteContactEmailAddress: externalLabData?.onSiteContactEmailAddress ?? '',
    notes: externalLabData?.notes ?? '',
    createdByEmail: externalLabData?.createdByEmail,
    createdDate: externalLabData?.createdDate,
    lastModifiedByEmail: externalLabData?.lastModifiedByEmail,
    lastModifiedDate: externalLabData?.lastModifiedDate
  };

  const externalLabValidationSchema = (invalidLabNames) => {
    return Yup.object().shape({
      laboratoryName: Yup.string()
        .transform((value) => (value ? value.trim() : ''))
        .required('Laboratory Name is required')
        .max(100, 'Must be 100 characters or less')
        .test("unique-name", "Laboratory Name already exists", (value) => {
          if (!value) return true;
          const lowerCaseValue = value.toLowerCase().trim();
          return !invalidLabNames.some((name) => name?.toLowerCase().trim() === lowerCaseValue);
        }),
      labCode: Yup.string()
        .transform((value) => (value ? value.trim() : ''))
        .matches(/^[a-zA-Z0-9][a-zA-Z0-9-_]{0,49}$/, "Only letters, numbers, '-' or '_' (max 50)")
        .test("unique-code", "Lab Code already exists", (value) => {
          if (!value) return true;
          const lowerCaseValue = value.toLowerCase().trim();
          return !invalidLabCodes.some((code) => code?.toLowerCase().trim() === lowerCaseValue);
        }),  
      labContactEmailAddress: Yup.string()
        .transform((value) => (value ? value.trim() : ''))
        .email('Invalid email format')
        .max(60, 'Email must be 60 characters or less')
        .test("domain-email", "Should be non ExxonMobil email", (value) => {
          if (!value) return true;
          const lowerCaseValue = value.toLowerCase().trim();
          return !exxonMobilDomains.some((domain) => lowerCaseValue.endsWith(domain));
        })
    });
  }

  return (
    <Formik
      initialValues={initialValues}
      innerRef={formikLabRef}
      enableReinitialize={true}
      validationSchema={() => externalLabValidationSchema(invalidExternalLabNames)}
    >
      {({ values, handleChange, handleBlur, setFieldValue, errors, touched, isValid, dirty }) => (
        <Form style={{margin:"10px", width: "60rem"}}>

          <StyledBox>
            <TextField
              style={{width: "35rem"}}
              name="laboratoryName"
              label="* Laboratory name"
              placeholder="Laboratory name"
              variant="outlined" size="small" margin="none" 
              value={values.laboratoryName}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={!isNewExternalLab}
              InputLabelProps={{ shrink: true }}
              error={touched.laboratoryName && Boolean(errors.laboratoryName)}
              helperText={touched.laboratoryName && errors.laboratoryName}
            />

            <TextField
              style={{width: "14rem", marginLeft:"20px"}}
              name="labCode"
              label="Lab Code"
              placeholder="Lab Code"
              variant="outlined" size="small" margin="none" 
              value={values.labCode}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={!canEdit}
              InputLabelProps={{ shrink: true }}
              error={touched.labCode && Boolean(errors.labCode)}
              helperText={touched.labCode && errors.labCode}
            />

            <HelpIcon
              style={{ marginTop: ".5rem", marginLeft: "10px", color: "blue" }}
              onClick={(e) => {
                setModalContent({
                  title: "Lab Code info",
                  message: "The Lab Code is used to uniquely identify a laboratory within Midas External Testing Processor (METP) system.",
                })
                setIsModalOpen(true);
              }}
            />

            <Box style={{ justifyContent: "space-between" }}>
              <FormControlLabel
                label="Active"
                style={{marginLeft:"25px"}}
                control={
                  <Checkbox
                    onChange={handleChange}
                    name="isActive"
                    checked={values.isActive}
                    disabled={!canEdit} 
                  />}
              />
            </Box>          
          </StyledBox>

          <StyledBox>
            <TextField
              style={{width: "40rem"}}
              name="labContactEmailAddress"
              label="Lab Contact Email"
              placeholder="Lab Contact Email"
              variant="outlined" size="small" margin="none" 
              value={values.labContactEmailAddress}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={!canEdit}
              InputLabelProps={{ shrink: true }}
              error={touched.labContactEmailAddress && Boolean(errors.labContactEmailAddress)}
              helperText={touched.labContactEmailAddress && errors.labContactEmailAddress}
            />

            <EmailAddressTextField
              style={{marginLeft:"20px"}}
              key='onSiteContactEmailAddress'
              validatedUserEmail={values.onSiteContactEmailAddress}
              setValidatedUserEmail={(value) => {
                setFieldValue('onSiteContactEmailAddress', value);
              }}
              setHasErrors={setOnSiteContactEmailError}
              hasErrors={onSiteContactEmailError}
              isDisabled={!canEdit}
              isRequired={false}
              allowNonXOMEmail={false}
              labelText="ExxonMobil Contact Email"
              placeholderText="ExxonMobil Contact Email"
              size={"small"}
              margin={"none"}
              fontSize={16}
              fieldWidth={"78%"}
              showPlusMeButton={true}
            /> 
          </StyledBox>

          <StyledBox>          
            <TextField
              style={{width: "60rem"}}
              name="notes"
              label="Notes"
              placeholder="Notes"
              variant="outlined" size="small" margin="none" 
              value={values.notes}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={!canEdit}
              InputLabelProps={{ shrink: true }}
            />
          </StyledBox>

          <AuditInfoLabel auditObject={values} />

          {isModalOpen && (
            <ModalSimpleButton
              title={modalContent?.title}
              buttonText="Ok"
              buttonAction={() => {
                setIsModalOpen(false);
              }}
              setOpen={setIsModalOpen}
              open={isModalOpen}>
              <Typography style={{ padding: "0 16px", fontSize: 18, textAlign: "center" }}>
                {modalContent?.message}
              </Typography>
            </ModalSimpleButton>
          )}
    
        </Form>
      )}
    </Formik>
  );
};

export default ExternalLaboratoryForm;
